import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Plus, 
  CreditCard, 
  BarChart3, 
  Eye, 
  QrCode,
  Pencil,
  Trash2,
  ExternalLink,
  TrendingUp,
  Nfc
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardPage = () => {
  const { getAuthHeaders } = useAuth();
  const [cards, setCards] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteCardId, setDeleteCardId] = useState(null);

  const fetchData = async () => {
    try {
      const [cardsRes, overviewRes] = await Promise.all([
        axios.get(`${API_URL}/api/cards`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/analytics/overview`, { headers: getAuthHeaders() })
      ]);
      setCards(cardsRes.data);
      setOverview(overviewRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDelete = async () => {
    if (!deleteCardId) return;
    
    try {
      await axios.delete(`${API_URL}/api/cards/${deleteCardId}`, { headers: getAuthHeaders() });
      toast.success('Card deleted successfully');
      setDeleteCardId(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete card');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="loading-spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
            <p className="text-muted-foreground mt-1">Manage your digital business cards</p>
          </div>
          <Link to="/cards/new">
            <Button className="btn-glow gap-2" data-testid="create-card-btn">
              <Plus className="w-5 h-5" />
              Create Card
            </Button>
          </Link>
        </div>

        {/* Stats Grid */}
        <div className="stats-grid">
          <Card className="border-border/50 hover:shadow-md transition-shadow duration-200">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Cards</CardTitle>
              <CreditCard className="w-5 h-5 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" data-testid="total-cards-count">{overview?.total_cards || 0}</div>
            </CardContent>
          </Card>

          <Card className="border-border/50 hover:shadow-md transition-shadow duration-200">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Scans</CardTitle>
              <Eye className="w-5 h-5 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" data-testid="total-scans-count">{overview?.total_scans || 0}</div>
            </CardContent>
          </Card>

          <Card className="border-border/50 hover:shadow-md transition-shadow duration-200">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Scans Today</CardTitle>
              <TrendingUp className="w-5 h-5 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" data-testid="scans-today-count">{overview?.scans_today || 0}</div>
            </CardContent>
          </Card>
        </div>

        {/* Cards Section */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Your Cards</h2>
          
          {cards.length === 0 ? (
            <Card className="empty-state border-dashed">
              <QrCode className="w-16 h-16 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-semibold mb-2">No cards yet</h3>
              <p className="text-muted-foreground mb-6 max-w-sm">
                Create your first digital business card to start sharing your contact information via QR code or NFC.
              </p>
              <Link to="/cards/new">
                <Button className="gap-2" data-testid="create-first-card-btn">
                  <Plus className="w-5 h-5" />
                  Create Your First Card
                </Button>
              </Link>
            </Card>
          ) : (
            <div className="card-grid">
              {cards.map((card, index) => (
                <Card 
                  key={card.id} 
                  className={`group hover:shadow-lg transition-all duration-300 border-border/50 tracing-beam ${index === 0 ? 'active' : ''}`}
                  data-testid={`card-item-${card.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      {card.profile_photo ? (
                        <img 
                          src={card.profile_photo} 
                          alt={card.name}
                          className="w-16 h-16 rounded-full object-cover ring-2 ring-border"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center ring-2 ring-border">
                          <span className="text-2xl font-bold text-primary">
                            {card.name?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-lg truncate">{card.name}</h3>
                        {card.job_title && (
                          <p className="text-muted-foreground text-sm truncate">{card.job_title}</p>
                        )}
                        {card.company && (
                          <p className="text-muted-foreground text-sm truncate">{card.company}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mt-4">
                      <Badge variant="secondary" className="gap-1">
                        <Eye className="w-3 h-3" />
                        {card.scan_count} scans
                      </Badge>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-border">
                      <Link to={`/cards/${card.id}/edit`} className="flex-1 min-w-[70px]">
                        <Button variant="outline" size="sm" className="w-full gap-1 text-xs sm:text-sm sm:gap-2" data-testid={`edit-card-${card.id}`}>
                          <Pencil className="w-3 h-3 sm:w-4 sm:h-4" />
                          Edit
                        </Button>
                      </Link>
                      <Link to={`/analytics/${card.id}`} className="flex-1 min-w-[70px]">
                        <Button variant="outline" size="sm" className="w-full gap-1 text-xs sm:text-sm sm:gap-2" data-testid={`analytics-card-${card.id}`}>
                          <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4" />
                          Stats
                        </Button>
                      </Link>
                      <a 
                        href={`/card/${card.id}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex-1 min-w-[70px]"
                      >
                        <Button variant="outline" size="sm" className="w-full gap-1 text-xs sm:text-sm sm:gap-2" data-testid={`view-card-${card.id}`}>
                          <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4" />
                          View
                        </Button>
                      </a>
                      <Link to={`/cards/${card.id}/nfc`} className="flex-1 min-w-[70px]">
                        <Button variant="outline" size="sm" className="w-full gap-1 text-xs sm:text-sm sm:gap-2 text-[#00A78B] border-[#00A78B]/30 hover:bg-[#00A78B]/10" data-testid={`nfc-card-${card.id}`}>
                          <Nfc className="w-3 h-3 sm:w-4 sm:h-4" />
                          NFC
                        </Button>
                      </Link>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => setDeleteCardId(card.id)}
                        data-testid={`delete-card-${card.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteCardId} onOpenChange={() => setDeleteCardId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this card?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. All analytics data for this card will also be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default DashboardPage;
